import { Box, Checkbox, TableCell } from "@mui/material";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../app/hooks";
import TableLinkRow from "../../common/components/functional/tableLinkRow/TableLinkRow";
import TalentStatusIcon, {
  StatusTextStyle,
} from "../../common/components/functional/talentStatus/TalentStatusIcon";
import { getName } from "../../common/helpers/common";
import {
  leadingZerosDateFormat,
  shortDateFormat,
} from "../../common/helpers/formatDateTime";
import { TalentNameWithPreview } from "../orders/editOrder/long/talentsStatus/TalentNameWithPreview";
import { hasPoolPro } from "./helpers";
import { PoolProPopper } from "./PoolProPopper";
import { Talent } from "./talentAPI";
import { TalentStatus } from "./types/common";

interface Props {
  talent: Talent;
  id?: string;
  selectableRows?: boolean;
  isSelected?: boolean;
  onSelect?: () => void;
}

export const TalentTableRow = ({
  talent,
  selectableRows,
  isSelected,
  onSelect,
}: Props) => {
  const { t } = useTranslation("talentProfile");
  const path = `/home/talents/${talent.id}/profile`;
  const talentTags = useAppSelector((state) => state.talentTags.entities);
  const translatedTags =
    talent?.account?.tags
      ?.map((tagId: string) => talentTags[tagId]?.name || tagId)
      .join(", ") || "";
  const listStatusTextKeys: Partial<Record<TalentStatus, string>> = {
    [TalentStatus.ACTIVE]: "ACTIVE_listStatusText",
    [TalentStatus.LATER]: "LATER_listStatusText",
    [TalentStatus.EMPLOYED_ELSEWHERE]: "EMPLOYED_ELSEWHERE_listStatusText",
    [TalentStatus.NOT_EMPLOYED]: "NOT_EMPLOYED_listStatusText",
  };
  const listStatusTextKey = listStatusTextKeys[talent.account.status];
  const listStatusTextOverride =
    talent.account.status === TalentStatus.NEW
      ? t("NEW")
      : listStatusTextKey && talent.account.statusChanged
        ? t(listStatusTextKey, {
            date: format(
              new Date(talent.account.statusChanged),
              leadingZerosDateFormat,
            ),
          })
        : undefined;

  return (
    <TableLinkRow to={path}>
      {[
        ...(selectableRows
          ? [
              <TableCell
                key="select"
                padding="checkbox"
                onClick={(e) => e.stopPropagation()}
              >
                <Checkbox
                  checked={!!isSelected}
                  onChange={onSelect}
                  disabled={talent.account.status !== TalentStatus.ACTIVE}
                />
              </TableCell>,
            ]
          : []),
        <TableCell key="name">
          <Box display="flex" alignItems="center" gap="2px">
            <TalentNameWithPreview
              talentId={talent.id}
              fullName={getName(talent.account)}
              disableLink
            />
            {hasPoolPro(talent) && <PoolProPopper talent={talent} />}
          </Box>
        </TableCell>,
        <TableCell key="phone">{talent.account.phoneNumber}</TableCell>,
        <TableCell key="email">{talent.account.emailAddress}</TableCell>,
        <TableCell
          width="20%"
          key="status"
          sx={{
            "& .MuiTypography-root": { fontSize: "14px" },
          }}
        >
          <TalentStatusIcon
            status={talent.account?.status}
            statusChanged={talent.account.statusChanged}
            statusTextStyle={StatusTextStyle.LONG}
            statusTextOverride={listStatusTextOverride}
          />
        </TableCell>,
        <TableCell key="memberSince">
          {talent.memberSince
            ? format(new Date(talent.memberSince), shortDateFormat)
            : ""}
        </TableCell>,
        <TableCell key="hr">
          {getName(talent.hrRepresentative || {})}
        </TableCell>,
        <TableCell key="city">{talent.account.address?.city}</TableCell>,
        <TableCell key="tags">{translatedTags}</TableCell>,
      ]}
    </TableLinkRow>
  );
};
